import { useState, useRef, useMemo } from 'react';
import { s } from '../constants/styles';
import { todayStr, fmt } from '../utils/date';
import { getOverloadSuggestion, isPR, getExerciseHistory, findLastSession } from '../utils/workout';
import FormGuide from './FormGuide';
import RestTimer from './RestTimer';
import LineChart from './LineChart';

export default function LoggingScreen({ exercise, sessions, dayIndex, dayColor: passedDayColor, startingWeights, onSave, onClose, restDefaults, timerEndTime, timerTotalSecs, onStartTimer, onDismissTimer }) {
  const today = todayStr();
  const session = sessions[today];
  const exData = session?.exercises?.[exercise.id];
  const logged = exData?.sets || [];

  const lastSess = findLastSession(sessions, dayIndex, today);
  const lastSets = lastSess?.exercises?.[exercise.id]?.sets || [];
  const lastWorking = lastSets.filter(s => !s.warmup);
  const sug = getOverloadSuggestion(exercise, sessions, startingWeights);

  const defW = logged.length > 0 ? logged[logged.length - 1].weight : (sug?.weight ?? (lastWorking[0]?.weight || 0));
  const defR = logged.length > 0 ? logged[logged.length - 1].reps : (lastWorking[0]?.reps || exercise.rLo);

  const [weight, setWeight] = useState(defW);
  const [weightStr, setWeightStr] = useState(String(defW));
  const [reps, setReps] = useState(defR);
  const [warmup, setWarmup] = useState(false);
  const [warmupTarget, setWarmupTarget] = useState("");
  const [view, setView] = useState("log");
  const [editing, setEditing] = useState(null);
  const scrollRef = useRef(null);

  const [showVideo, setShowVideo] = useState(false);
  const rawQuery = encodeURIComponent(exercise.name + ' proper form cues');
  const videoUrl = exercise.videoUrl
    ? exercise.videoUrl.replace('youtube.com/results?search_query=', 'youtube.com/embed?listType=search&list=')
    : `https://www.youtube.com/embed?listType=search&list=${rawQuery}`;
  const workingDone = logged.filter(s => !s.warmup).length;
  const history = getExerciseHistory(sessions, exercise.id);
  const dayColor = passedDayColor || "#4f8fff";
  const repLabel = exercise.perLeg ? `${exercise.rLo}/leg` : `${exercise.rLo}${exercise.rHi !== exercise.rLo ? "-" + exercise.rHi : ""}`;
  const timerActive = timerEndTime && timerEndTime > Date.now() - 3000;

  // All-time PR: best weight × best reps ever logged for this exercise
  const allTimePR = useMemo(() => {
    let best = null;
    for (const [, sess] of Object.entries(sessions)) {
      const sets = sess.exercises?.[exercise.id]?.sets || [];
      for (const st of sets) {
        if (!st.warmup && st.weight > 0) {
          if (!best || st.weight > best.weight || (st.weight === best.weight && st.reps > best.reps)) {
            best = { weight: st.weight, reps: st.reps };
          }
        }
      }
    }
    return best;
  }, [sessions, exercise.id]);

  // Session volume: sum of weight × reps for all working sets this session
  const sessionVolume = logged
    .filter(s => !s.warmup)
    .reduce((sum, s) => sum + s.weight * s.reps, 0);

  const chartPoints = history.slice().reverse().map(h => {
    const ws = h.sets.filter(s => !s.warmup);
    if (!ws.length) return null;
    return { y: ws[ws.length - 1].weight, label: fmt(h.date) };
  }).filter(Boolean);

  function logSet() {
    const pr = !warmup && isPR(exercise.id, weight, reps, sessions);
    const ns = { weight, reps, warmup, pr, ts: Date.now() };
    const updated = editing !== null ? logged.map((st, i) => i === editing ? ns : st) : [...logged, ns];
    onSave(exercise.id, updated, null, exercise.name);
    setEditing(null);
    const newWorkingCount = updated.filter(s => !s.warmup).length;
    const isLastSet = !warmup && newWorkingCount >= exercise.sets;
    if (!warmup && editing === null && !isLastSet) onStartTimer(exercise.compound ? restDefaults.compound : restDefaults.isolation);
    setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }), 50);
  }

  function deleteSet(idx) { onSave(exercise.id, logged.filter((_, i) => i !== idx), null, exercise.name); }

  function dupLast() {
    if (!logged.length) return;
    const l = logged[logged.length - 1];
    const pr = !l.warmup && isPR(exercise.id, l.weight, l.reps, sessions);
    const updated = [...logged, { ...l, pr, ts: Date.now() }];
    onSave(exercise.id, updated, null, exercise.name);
    const newWorkingCount = updated.filter(s => !s.warmup).length;
    const isLastSet = !l.warmup && newWorkingCount >= exercise.sets;
    if (!l.warmup && !isLastSet) onStartTimer(exercise.compound ? restDefaults.compound : restDefaults.isolation);
  }

  function startEdit(idx) {
    const x = logged[idx];
    setWeight(x.weight); setWeightStr(String(x.weight)); setReps(x.reps); setWarmup(x.warmup); setEditing(idx);
  }

  return (
    <div className="slide-up" style={{ position: "fixed", inset: 0, zIndex: 60, background: "var(--bg-0)", display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", paddingTop: "max(12px, env(safe-area-inset-top))", flexShrink: 0 }}>
        <button onClick={onClose} style={{ ...s.btnG, padding: "10px 16px", fontSize: 14, display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 18, lineHeight: 1 }}>‹</span> Back
        </button>
        <div style={{ display: "flex", gap: 4 }}>
          {["log", "history"].map(v => (
            <button key={v} onClick={() => setView(v)} style={{
              padding: "8px 14px", borderRadius: 10, fontSize: 13, fontWeight: 600, border: "none", cursor: "pointer", fontFamily: "inherit",
              background: view === v ? "rgba(79,143,255,0.12)" : "transparent",
              color: view === v ? "#4f8fff" : "var(--text-3)",
            }}>{v === "log" ? "Log" : "History"}</button>
          ))}
        </div>
      </div>

      <div style={{ padding: "0 20px 10px", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <h2 style={{ fontSize: 21, fontWeight: 800, flex: 1, lineHeight: 1.2 }}>{exData?.swappedTo || exercise.name}</h2>
          {(exercise.videoUrl || exercise.name) && (
            <button onClick={() => setShowVideo(true)} style={{
              fontSize: 11, padding: "5px 10px", borderRadius: 8, border: "1px solid rgba(79,143,255,0.25)",
              background: "rgba(79,143,255,0.1)", color: "var(--accent)", cursor: "pointer", fontFamily: "inherit", fontWeight: 700,
              WebkitUserSelect: "none", userSelect: "none",
            }}>▶ Form</button>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, fontWeight: 700, padding: "4px 10px", borderRadius: 8, background: `${dayColor}18`, color: dayColor }}>
            {exercise.sets} x {repLabel}
          </span>
          <div style={{ display: "flex", gap: 3 }}>
            {Array.from({ length: exercise.sets }).map((_, i) => (
              <div key={i} style={{ width: 8, height: 8, borderRadius: 4, background: i < workingDone ? "#34d399" : "rgba(255,255,255,0.08)", transition: "all 0.2s" }} />
            ))}
          </div>
          {exercise.assisted && <span style={{ fontSize: 10, padding: "3px 7px", borderRadius: 6, background: "rgba(167,139,250,0.12)", color: "#a78bfa", fontWeight: 700 }}>Assisted</span>}
        </div>

        {/* PR + Volume row */}
        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          {allTimePR && (
            <div style={{
              flex: 1, padding: "8px 12px", borderRadius: 10,
              background: "rgba(251,191,36,0.06)", border: "1px solid rgba(251,191,36,0.15)",
              display: "flex", flexDirection: "column", gap: 1,
            }}>
              <div style={{ fontSize: 9, fontWeight: 800, color: "var(--amber)", letterSpacing: 0.8 }}>ALL-TIME PR</div>
              <div style={{ fontSize: 15, fontWeight: 800, color: "var(--text-1)" }}>
                {allTimePR.weight} <span style={{ fontSize: 11, color: "var(--text-3)", fontWeight: 600 }}>lbs</span>
                <span style={{ fontSize: 13, color: "var(--text-3)", fontWeight: 600, marginLeft: 4 }}>× {allTimePR.reps}</span>
              </div>
            </div>
          )}
          {sessionVolume > 0 && (
            <div style={{
              flex: 1, padding: "8px 12px", borderRadius: 10,
              background: "rgba(79,143,255,0.06)", border: "1px solid rgba(79,143,255,0.15)",
              display: "flex", flexDirection: "column", gap: 1,
            }}>
              <div style={{ fontSize: 9, fontWeight: 800, color: "var(--accent)", letterSpacing: 0.8 }}>SESSION VOLUME</div>
              <div style={{ fontSize: 15, fontWeight: 800, color: "var(--text-1)" }}>
                {sessionVolume.toLocaleString()} <span style={{ fontSize: 11, color: "var(--text-3)", fontWeight: 600 }}>lbs</span>
              </div>
            </div>
          )}
        </div>
        {sug && (
          <div style={{
            marginTop: 8, padding: "8px 12px", borderRadius: 10, fontSize: 12, fontWeight: 600,
            background: sug.action === "up" ? "var(--green-dim)" : sug.action === "down" ? "var(--red-dim)" : "var(--bg-2)",
            color: sug.action === "up" ? "var(--green)" : sug.action === "down" ? "var(--red)" : "var(--text-2)",
            display: "flex", alignItems: "center", gap: 6,
          }}>
            <span style={{ fontWeight: 800, fontSize: 14 }}>{sug.action === "up" ? "↑" : sug.action === "down" ? "↓" : "→"}</span>
            {sug.action === "up" ? `Target: ${sug.msg}` : sug.msg}
          </div>
        )}
      </div>

      {view === "history" ? (
        <div style={{ flex: 1, overflow: "auto", padding: "0 20px 24px" }}>
          {chartPoints.length >= 2 && (
            <div style={{ ...s.card, padding: "14px 14px 10px", marginBottom: 12 }}>
              <div style={{ ...s.label, marginBottom: 8 }}>WEIGHT TREND</div>
              <LineChart points={chartPoints} color={dayColor} unit=" lbs" />
            </div>
          )}
          {history.length === 0 && <p style={{ color: "var(--text-3)", textAlign: "center", marginTop: 40 }}>No history yet</p>}
          {history.map((h, i) => (
            <div key={i} className="fade-in" style={{ ...s.card, padding: 14, marginBottom: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-3)", marginBottom: 6 }}>{fmt(h.date)}</div>
              {h.sets.map((st, j) => (
                <div key={j} style={{ display: "flex", gap: 8, fontSize: 14, padding: "2px 0", color: st.warmup ? "var(--text-3)" : "var(--text-1)" }}>
                  <span style={{ width: 18, fontWeight: 700, color: "var(--text-3)" }}>{st.warmup ? "W" : j + 1}</span>
                  <span style={{ fontWeight: 700 }}>{st.weight}</span><span style={{ color: "var(--text-3)" }}>x</span><span style={{ fontWeight: 700 }}>{st.reps}</span>
                  {st.pr && <span style={{ color: "var(--amber)", fontSize: 10, fontWeight: 800 }}>PR</span>}
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : (
        <>
          <div ref={scrollRef} style={{ flex: 1, overflow: "auto", padding: "0 16px 12px" }}>
            {lastWorking.length > 0 && (
              <div style={{ padding: "8px 12px", borderRadius: 10, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.03)", marginBottom: 8 }}>
                <div style={{ ...s.label, marginBottom: 4, fontSize: 10 }}>PREV {lastSess ? fmt(lastSess.date) : ""}</div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {lastSets.map((ls, i) => (
                    <span key={i} style={{ fontSize: 12, color: ls.warmup ? "var(--text-3)" : "var(--text-2)", fontWeight: 600 }}>
                      {ls.warmup ? "W:" : ""}{ls.weight}x{ls.reps}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {logged.map((st, i) => {
              const wIdx = logged.slice(0, i + 1).filter(x => !x.warmup).length;
              return (
                <div key={i} className="fade-in" style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "12px 14px", borderRadius: 14, marginBottom: 5,
                  background: st.warmup ? "rgba(167,139,250,0.04)" : "var(--bg-2)",
                  border: st.pr ? "1px solid rgba(251,191,36,0.25)" : "1px solid rgba(255,255,255,0.03)",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <span style={{ width: 22, fontSize: 11, fontWeight: 800, color: st.warmup ? "var(--purple)" : "var(--text-3)", textAlign: "center" }}>
                      {st.warmup ? "W" : wIdx}
                    </span>
                    <span style={{ fontSize: 17, fontWeight: 700 }}>{st.weight}</span>
                    <span style={{ color: "var(--text-3)" }}>x</span>
                    <span style={{ fontSize: 17, fontWeight: 700 }}>{st.reps}</span>
                    {st.pr && <span style={{ fontSize: 9, fontWeight: 800, padding: "2px 6px", borderRadius: 5, background: "var(--amber-dim)", color: "var(--amber)" }}>PR!</span>}
                  </div>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button onClick={() => startEdit(i)} style={{ width: 34, height: 34, borderRadius: 9, border: "none", background: "rgba(255,255,255,0.04)", color: "var(--text-3)", fontSize: 14, cursor: "pointer" }}>✎</button>
                    <button onClick={() => deleteSet(i)} style={{ width: 34, height: 34, borderRadius: 9, border: "none", background: "rgba(248,113,113,0.06)", color: "var(--red)", fontSize: 13, cursor: "pointer" }}>✕</button>
                  </div>
                </div>
              );
            })}
            {logged.length > 0 && (
              <button onClick={dupLast} style={{
                width: "100%", padding: 12, borderRadius: 12, fontSize: 13, fontWeight: 600, fontFamily: "inherit",
                background: "none", border: "1px dashed rgba(255,255,255,0.07)", color: "var(--text-3)", cursor: "pointer",
              }}>Duplicate Last Set</button>
            )}
          </div>

          {timerActive && <RestTimer endTime={timerEndTime} totalSeconds={timerTotalSecs} onDismiss={onDismissTimer} />}

          <div style={{
            flexShrink: 0, background: "var(--bg-0)",
            padding: "14px 16px", paddingBottom: "max(14px, env(safe-area-inset-bottom))",
            borderTop: "1px solid rgba(255,255,255,0.04)",
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 5, marginBottom: 10 }}>
              {[10, 5, 2.5].map(v => (
                <button key={v} onClick={() => { const nw = Math.max(0, +(weight - v).toFixed(1)); setWeight(nw); setWeightStr(String(nw)); }}
                  style={{ ...s.btnG, width: 48, height: 42, fontSize: 12, fontWeight: 700, color: "var(--red)", padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  -{v}
                </button>
              ))}
              <div style={{ textAlign: "center", width: 76 }}>
                <input type="number" inputMode="decimal" value={weightStr}
                  onChange={e => { setWeightStr(e.target.value); setWeight(parseFloat(e.target.value) || 0); }}
                  onFocus={e => e.target.select()}
                  onBlur={() => setWeightStr(String(weight))}
                  style={{ width: "100%", textAlign: "center", fontSize: 26, fontWeight: 800, background: "transparent", border: "none", borderBottom: "2px solid rgba(79,143,255,0.3)", color: "var(--text-1)", fontFamily: "inherit", fontVariantNumeric: "tabular-nums", padding: "2px 0", outline: "none" }} />
                <div style={{ fontSize: 9, fontWeight: 600, color: "var(--text-3)", textTransform: "uppercase", marginTop: 2 }}>
                  {exercise.assisted ? "assist" : "lbs"}
                </div>
              </div>
              {[2.5, 5, 10].map(v => (
                <button key={v} onClick={() => { const nw = +(weight + v).toFixed(1); setWeight(nw); setWeightStr(String(nw)); }}
                  style={{ ...s.btnG, width: 48, height: 42, fontSize: 12, fontWeight: 700, color: "var(--green)", padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  +{v}
                </button>
              ))}
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 10 }}>
              <button onClick={() => setReps(r => Math.max(1, r - 1))}
                style={{ ...s.btnG, width: 54, height: 54, fontSize: 20, fontWeight: 800, color: "var(--red)", padding: 0, borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center" }}>-</button>
              <div style={{ textAlign: "center", width: 76 }}>
                <div style={{ fontSize: 34, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{reps}</div>
                <div style={{ fontSize: 9, fontWeight: 600, color: "var(--text-3)" }}>REPS ({exercise.rLo}-{exercise.rHi})</div>
              </div>
              <button onClick={() => setReps(r => r + 1)}
                style={{ ...s.btnG, width: 54, height: 54, fontSize: 20, fontWeight: 800, color: "var(--green)", padding: 0, borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center" }}>+</button>
              <button onClick={() => { const next = !warmup; setWarmup(next); if (next && sug?.weight > 0) setWarmupTarget(String(sug.weight)); }} style={{
                width: 54, height: 54, borderRadius: 14, border: "none", fontSize: 10, fontWeight: 800, cursor: "pointer", fontFamily: "inherit",
                background: warmup ? "rgba(167,139,250,0.18)" : "var(--bg-2)",
                color: warmup ? "#a78bfa" : "var(--text-3)", letterSpacing: 0.3,
              }}>{warmup ? "WARM" : "WORK"}</button>
            </div>

            {/* Warmup suggestions */}
            {warmup && (() => {
              const target = parseFloat(warmupTarget);
              const round = w => exercise.compound ? Math.round(w / 5) * 5 : Math.round(w / 2.5) * 2.5;
              const sets = !isNaN(target) && target > 0 ? [
                { pct: 50, reps: 8, w: round(target * 0.5) },
                { pct: 65, reps: 5, w: round(target * 0.65) },
                { pct: 80, reps: 3, w: round(target * 0.8) },
              ] : null;
              return (
                <div className="fade-in" style={{ marginTop: 8, padding: "10px 12px", borderRadius: 12, background: "rgba(167,139,250,0.06)", border: "1px solid rgba(167,139,250,0.15)" }}>
                  <div style={{ fontSize: 9, fontWeight: 800, color: "#a78bfa", letterSpacing: 0.8, marginBottom: 8 }}>WARMUP CALCULATOR</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: sets ? 10 : 0 }}>
                    <input
                      type="number" inputMode="decimal"
                      value={warmupTarget}
                      onChange={e => setWarmupTarget(e.target.value)}
                      placeholder="Working weight (lbs)"
                      style={{ ...s.inp, flex: 1, fontSize: 14, padding: "8px 12px", background: "rgba(255,255,255,0.05)" }}
                    />
                  </div>
                  {sets && (
                    <div style={{ display: "flex", gap: 6 }}>
                      {sets.map(st => (
                        <button key={st.pct} onClick={() => { setWeight(st.w); setWeightStr(String(st.w)); setReps(st.reps); }} style={{
                          flex: 1, padding: "8px 4px", borderRadius: 10, border: "1px solid rgba(167,139,250,0.2)",
                          background: "rgba(167,139,250,0.08)", cursor: "pointer", fontFamily: "inherit",
                        }}>
                          <div style={{ fontSize: 13, fontWeight: 800, color: "var(--text-1)" }}>{st.w}</div>
                          <div style={{ fontSize: 10, color: "var(--text-3)", fontWeight: 600 }}>× {st.reps}</div>
                          <div style={{ fontSize: 9, color: "#a78bfa", fontWeight: 700, marginTop: 2 }}>{st.pct}%</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
            <button onClick={logSet} style={{
              ...s.btnP, width: "100%", height: 52, fontSize: 16, fontFamily: "inherit",
              background: editing !== null ? "linear-gradient(135deg, #d97706, #b45309)" : s.btnP.background,
            }}>{editing !== null ? "Update Set" : `Log ${warmup ? "Warmup" : "Set"}`}</button>
            {editing !== null && (
              <button onClick={() => { setEditing(null); setWeight(defW); setWeightStr(String(defW)); setReps(defR); setWarmup(false); }}
                style={{ width: "100%", padding: 10, border: "none", background: "none", color: "var(--text-3)", fontSize: 13, cursor: "pointer", fontFamily: "inherit", marginTop: 2 }}>Cancel</button>
            )}
          </div>
        </>
      )}

      {showVideo && (
        <FormGuide exercise={exercise} dayIndex={dayIndex} dayColor={dayColor} onClose={() => setShowVideo(false)} displayName={exData?.swappedTo || exercise.name} />
      )}
    </div>
  );
}
